<script setup>
import { computed, ref, watch } from 'vue'
import { useForm, router } from '@inertiajs/vue3'
import { Trash2, Check, Plus } from 'lucide-vue-next'

/*
  Formulário de um registro da ordem de serviço.

  Etapas, tarefas, componentes, parcelas e histórico têm a mesma forma — campos
  simples, um salvar e um remover — e mudam só na lista de campos. Em vez de
  cinco componentes quase idênticos, o esquema vem por prop.

  `fields` aceita: text, textarea, number, date, datetime-local, select, checkbox.
*/
const props = defineProps({
  fields: { type: Array, required: true },
  record: { type: Object, default: null },
  paramKey: { type: String, required: true },
  action: { type: String, required: true },
  deleteUrl: { type: String, default: '' },
  deleteConfirm: { type: String, default: 'Remover este item?' },
  submitLabel: { type: String, default: '' },
})

const inicial = () =>
  Object.fromEntries(
    props.fields.map((f) => [f.name, props.record?.[f.name] ?? (f.type === 'checkbox' ? false : '')]),
  )

const form = useForm({ [props.paramKey]: inicial() })
const novo = computed(() => props.record === null)

// Depois que o servidor devolve a lista atualizada, o formulário de edição
// precisa refletir o que foi salvo (e o de criação, esvaziar).
watch(
  () => props.record,
  () => {
    form[props.paramKey] = inicial()
  },
  { deep: true },
)

const salvar = () => {
  const opcoes = { preserveScroll: true, onSuccess: () => novo.value && form.reset() }
  novo.value ? form.post(props.action, opcoes) : form.patch(props.action, opcoes)
}

const remover = () => {
  if (confirm(props.deleteConfirm)) router.delete(props.deleteUrl, { preserveScroll: true })
}

const uid = ref(`f${Math.random().toString(36).slice(2, 8)}`)
const idDe = (name) => `${uid.value}-${name}`
const erro = (name) => form.errors[name]
const classe = (f) =>
  [
    'mt-1 w-full rounded-lg border px-3 py-2 text-sm text-up-navy outline-none focus:ring-2 focus:ring-up-orange/30',
    erro(f.name) ? 'border-up-orange focus:border-up-orange' : 'border-slate-300 focus:border-up-orange',
  ].join(' ')
</script>

<template>
  <form class="grid gap-3 sm:grid-cols-12" @submit.prevent="salvar">
    <div v-for="f in props.fields" :key="f.name" :class="f.span ?? 'sm:col-span-3'">
      <label :for="idDe(f.name)" class="block text-xs font-medium text-slate-500">{{ f.label }}</label>

      <textarea
        v-if="f.type === 'textarea'"
        :id="idDe(f.name)"
        v-model="form[props.paramKey][f.name]"
        rows="2"
        :required="f.required"
        :class="classe(f)"
      />

      <select
        v-else-if="f.type === 'select'"
        :id="idDe(f.name)"
        v-model="form[props.paramKey][f.name]"
        :required="f.required"
        :class="classe(f)"
      >
        <option v-if="!f.required" value="">—</option>
        <option v-for="o in f.options" :key="o.value" :value="o.value">{{ o.label }}</option>
      </select>

      <label v-else-if="f.type === 'checkbox'" class="mt-2 flex items-center gap-2 text-sm text-slate-600">
        <input
          :id="idDe(f.name)"
          v-model="form[props.paramKey][f.name]"
          type="checkbox"
          class="h-4 w-4 rounded border-slate-300 text-up-orange focus:ring-up-orange/30"
        >
        {{ f.checkboxLabel ?? 'Sim' }}
      </label>

      <input
        v-else
        :id="idDe(f.name)"
        v-model="form[props.paramKey][f.name]"
        :type="f.type ?? 'text'"
        :step="f.step"
        :min="f.min"
        :max="f.max"
        :required="f.required"
        :placeholder="f.placeholder"
        :class="classe(f)"
      >

      <p v-if="erro(f.name)" class="mt-1 text-xs text-up-orange-dark">{{ erro(f.name) }}</p>
      <p v-else-if="f.hint" class="mt-1 text-xs text-slate-400">{{ f.hint }}</p>
    </div>

    <div class="flex items-end gap-1 sm:col-span-2">
      <button
        type="submit"
        :disabled="form.processing"
        class="flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold transition-colors disabled:opacity-60"
        :class="novo
          ? 'bg-up-orange text-white hover:bg-up-orange-dark'
          : 'border border-slate-300 text-up-navy hover:border-up-orange hover:text-up-orange'"
      >
        <component :is="novo ? Plus : Check" class="h-4 w-4" aria-hidden="true" />
        {{ props.submitLabel || (novo ? 'Adicionar' : 'Salvar') }}
      </button>

      <button
        v-if="props.deleteUrl"
        type="button"
        class="rounded-lg p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-up-orange"
        aria-label="Remover"
        @click="remover"
      >
        <Trash2 class="h-4 w-4" />
      </button>
    </div>
  </form>
</template>
